import { inject, Injectable } from '@angular/core';
import { EntityState } from '@ngrx/entity';
import { Draft } from 'immer';

import {
  CanvasElement,
  CanvasElementId,
  SlotData,
  SlotIdOf,
  WrapperActionButton,
} from '@blaze-runtime/engine-domain';

import { hasProps } from '../../../canvas-elements/element-model/element-props-guards';
import {
  isElementType,
  isVariantOfFormContainer,
} from '../../../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../../../canvas-elements/element-model/wrapper-refs';
import { DIALOG_COLUMN_WIDTH } from '../../../elements/dialog/dialog.element';
import { FormContainerPropsModel } from '../../../elements/form-container/form-container.model';
import {
  COLUMN_CONTAINER_REFID,
  COMMIT_BUTTON_REFID,
  FindFieldTargetPathData,
} from '../../../utils/elements/generation.utils';
import { getPathFirstInSlot } from '../../../utils/layout/helpers';
import { placeElementMutably } from '../../../utils/layout/place-element';
import { removeElementMutablyWithoutSanitization } from '../../../utils/layout/remove-element';
import { ElementDefinitionsService } from '../../element-definitions.service';

import { getFieldTargetPath } from './form-field-placement';
import { type FormLikeElement } from './form-generation-data';

/** How many fields fit in one column before the form is spread over several. */
const MAX_FIELDS_PER_COLUMN = 15;
const COL_SPACING = 16;

/**
 * Lays a form's fields out in columns: creates the container that holds them, keeps the
 * parent dialog wide enough, and reflows the fields when the column count changes.
 */
@Injectable({ providedIn: 'root' })
export class FormColumnsLayoutService {
  private readonly elementDefinitions = inject(ElementDefinitionsService);

  /** Whether a form with this many fields is spread over columns rather than listed. */
  needsColumnContainer(fieldsCount: number): boolean {
    return fieldsCount > MAX_FIELDS_PER_COLUMN;
  }

  /** Updates the form elements columns layout */
  regenerateFormColumnsMutably(
    entityState: Draft<EntityState<CanvasElement>>,
    formId: CanvasElementId,
    parentDialogId: string | undefined,
  ): void {
    const form = entityState.entities[formId];
    if (!form || !hasProps<FormContainerPropsModel>(form)) {
      throw new Error('form element missing');
    }
    if (!isVariantOfFormContainer(form)) {
      throw new Error('Form element error');
    }

    const columnsCount = form.props.content.options?.columnsCount ?? 1;
    const refs = getWrapperRefs(form);
    const { breaks, fields } = this.collectFieldsToReflow(entityState, form);

    for (const childId of breaks) {
      // This doesn't sanitize the elements and connections
      // but it's not needed for breakline and currently it's difficult to do
      // without moving the form generation logic to a reducer
      removeElementMutablyWithoutSanitization(entityState, childId);
    }

    const totalFields = fields.length;
    refs[COLUMN_CONTAINER_REFID] = this.addOrUpdateColumnContainerMutably(
      refs[COLUMN_CONTAINER_REFID],
      entityState,
      form,
      totalFields,
      columnsCount,
    ).containerId;
    this.tryUpdateDialogWidthMutably(parentDialogId, columnsCount, entityState);

    const findFieldTargetPathData: FindFieldTargetPathData<FormLikeElement> = {
      element: form,
      isInitialGeneration: false,
      totalFields,
      fieldIndex: 0,
      isColumnsReflow: true,
    };
    // Update each input element
    for (let i = 0; i < fields.length; i++) {
      findFieldTargetPathData.fieldIndex = i;
      placeElementMutably(entityState, {
        elementId: fields[i],
        atPath: getFieldTargetPath(entityState, findFieldTargetPathData),
      });
    }
  }

  /**
   * Creates the column container when the form has none, and (re)builds its columns.
   * @param columnsCountOverride the column count to use; derived from the field count when
   * the caller has no preference
   */
  addOrUpdateColumnContainerMutably(
    containerId: CanvasElementId | undefined,
    entityState: EntityState<CanvasElement>,
    form: FormLikeElement,
    fieldsCount: number,
    columnsCountOverride?: number,
  ): { containerId: CanvasElementId; columnsCount: number } {
    if (!containerId) {
      containerId = this.addColumnContainerMutably(entityState, form);
    }

    const container = entityState.entities[containerId];
    if (!isElementType(container, 'container')) {
      throw new Error('Container creation error');
    }

    const columnsCount = columnsCountOverride ?? Math.ceil(fieldsCount / MAX_FIELDS_PER_COLUMN);

    // Prepare columns
    container.slots = { ids: [] };
    for (let i = 0; i < columnsCount; i++) {
      const key: SlotIdOf<'container'> = `${i}`;
      container.slots.ids.push(key);

      const props =
        i !== 0 // Excluding the first column, all others should have a left padding
          ? { style: { container: { offset: { padding: `0px 0px 0px ${COL_SPACING}px` } } } }
          : undefined;

      container.slots[key] = {
        children: [],
        props,
      };
    }

    return { containerId, columnsCount };
  }

  /** Widens the dialog a pregenerated form sits in, so its columns fit side by side. */
  tryUpdateDialogWidthMutably(
    parentDialogId: string | undefined,
    columnsCount: number,
    entityState: EntityState<CanvasElement>,
  ): void {
    if (!parentDialogId) {
      return;
    }
    const dialogElement = entityState.entities[parentDialogId];
    if (dialogElement) {
      dialogElement.width.value = columnsWidth(columnsCount);
    }
  }

  /**
   * Empties the form of everything the reflow will place again, leaving only the buttons.
   * @returns the field elements to place, and the break lines to drop
   */
  private collectFieldsToReflow(
    entityState: Draft<EntityState<CanvasElement>>,
    form: FormLikeElement,
  ): { breaks: CanvasElementId[]; fields: CanvasElementId[] } {
    const refs = getWrapperRefs(form);
    let elementsToMove: CanvasElementId[];

    if (refs[COLUMN_CONTAINER_REFID]) {
      const containerSlots = entityState.entities[refs[COLUMN_CONTAINER_REFID]]?.slots;
      elementsToMove =
        (containerSlots?.ids.flatMap((id) => containerSlots[id]?.children) as CanvasElementId[]) ??
        [];
    } else {
      const buttonsToLeave = Object.entries(refs)
        .filter(([key]) => key === COMMIT_BUTTON_REFID || WrapperActionButton.isRefId(key))
        .map(([, elementId]) => elementId);
      const formSlot = form.slots[form.slots.ids[0]] as SlotData;
      elementsToMove =
        formSlot?.children.filter(
          (elementId) => elementId && !buttonsToLeave.includes(elementId),
        ) ?? [];
      formSlot.children = buttonsToLeave;
    }

    return elementsToMove.reduce(
      (grouped, childId) => {
        if (entityState.entities[childId]?.type === 'breakLine') {
          grouped.breaks.push(childId);
        } else {
          grouped.fields.push(childId);
        }
        return grouped;
      },
      { fields: [] as CanvasElementId[], breaks: [] as CanvasElementId[] },
    );
  }

  private addColumnContainerMutably(
    entityState: EntityState<CanvasElement>,
    form: FormLikeElement,
  ): CanvasElementId {
    const containerDef = this.elementDefinitions.getDefinitionSafe('container');
    containerDef.name = 'Form column';
    containerDef.height = { ...containerDef.height, mode: 'full' };

    const targetPath = getPathFirstInSlot(entityState, form.id, form.slots.ids[0]);
    targetPath.targetPosition = { type: 'top' };

    const result = placeElementMutably(entityState, {
      elementDefinition: containerDef,
      atPath: targetPath,
    });
    if (!result || !result.elementId) {
      throw new Error('Failed to add column container');
    }

    return result.elementId;
  }
}

/** The width a dialog needs to show this many form columns. */
function columnsWidth(columnsCount: number): number {
  const columnWidth = DIALOG_COLUMN_WIDTH - COL_SPACING * 2;
  return (
    COL_SPACING * 2 + // side padding
    columnWidth * columnsCount +
    COL_SPACING * (columnsCount - 1)
  ); // space between cols
}
